import api from '../../services/api';
import { env } from '../../env';

const API_BASE_URL = (env?.REACT_APP_BACKEND_URL || "") + "/api/v1";

export const AUDIO_TRANSCRIPTION_ACTIONS = {
  SET_LOADING: 'SET_AUDIO_LOADING',
  SET_TRANSCRIBING: 'SET_AUDIO_TRANSCRIBING',
  SET_ERROR: 'SET_AUDIO_ERROR',
  SET_TRANSCRIPT: 'SET_AUDIO_TRANSCRIPT',
  SET_LANGUAGE: 'SET_AUDIO_LANGUAGE',
  SET_SUPPORTED_LANGUAGES: 'SET_AUDIO_SUPPORTED_LANGUAGES',
  SET_UPLOAD_PROGRESS: 'SET_AUDIO_UPLOAD_PROGRESS',
  SET_FILE: 'SET_AUDIO_FILE',
  RESET_STATE: 'RESET_AUDIO_STATE',
  CLEAR_TRANSCRIPT: 'CLEAR_AUDIO_TRANSCRIPT',
};

export const fetchSupportedLanguages = () => async (dispatch) => {
  try {
    const response = await api.get(`${API_BASE_URL}/audio-transcription/languages`);

    dispatch({
      type: AUDIO_TRANSCRIPTION_ACTIONS.SET_SUPPORTED_LANGUAGES,
      payload: response.data
    });
  } catch (error) {
    dispatch({
      type: AUDIO_TRANSCRIPTION_ACTIONS.SET_ERROR,
      payload: error.response?.data?.message || error.message || 'Failed to fetch supported languages'
    });
  }
};

export const transcribeAudio = (file, language = 'en-US') => async (dispatch) => {
  try {
    // Validate file
    const validation = validateAudioFile(file);
    if (!validation.valid) {
      dispatch({
        type: AUDIO_TRANSCRIPTION_ACTIONS.SET_ERROR,
        payload: validation.error
      });
      return;
    }

    dispatch({
      type: AUDIO_TRANSCRIPTION_ACTIONS.SET_FILE,
      payload: file
    });

    dispatch({
      type: AUDIO_TRANSCRIPTION_ACTIONS.SET_LOADING,
      payload: true
    });

    dispatch({
      type: AUDIO_TRANSCRIPTION_ACTIONS.SET_TRANSCRIBING,
      payload: true
    });

    const formData = new FormData();
    formData.append('audio', file);
    formData.append('language', language);

    const response = await api.post(`${API_BASE_URL}/audio-transcription/transcribe`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    dispatch({
      type: AUDIO_TRANSCRIPTION_ACTIONS.SET_TRANSCRIPT,
      payload: {
        transcript: response.data.transcript,
        confidence: response.data.confidence
      }
    });
  } catch (error) {
    dispatch({
      type: AUDIO_TRANSCRIPTION_ACTIONS.SET_ERROR,
      payload: error.response?.data?.message || error.message || 'Failed to transcribe audio'
    });
  } finally {
    dispatch({
      type: AUDIO_TRANSCRIPTION_ACTIONS.SET_LOADING,
      payload: false
    });

    dispatch({
      type: AUDIO_TRANSCRIPTION_ACTIONS.SET_TRANSCRIBING,
      payload: false
    });
  }
};

export const setLanguage = (language) => (dispatch) => {
  dispatch({
    type: AUDIO_TRANSCRIPTION_ACTIONS.SET_LANGUAGE,
    payload: language
  });
};

export const clearTranscript = () => (dispatch) => {
  dispatch({
    type: AUDIO_TRANSCRIPTION_ACTIONS.CLEAR_TRANSCRIPT
  });
};

export const resetAudioTranscriptionState = () => (dispatch) => {
  dispatch({
    type: AUDIO_TRANSCRIPTION_ACTIONS.RESET_STATE
  });
};

const validateAudioFile = (file) => {
  const allowedMimeTypes = [
    'audio/mpeg',
    'audio/mp3',
    'audio/wav',
    'audio/webm',
    'audio/ogg',
    'audio/flac',
    'audio/m4a',
    'audio/x-m4a',
    'audio/mp4',
    'audio/aac',
    'audio/opus',
    'video/mp4',
  ];

  const maxSizeMB = 25;
  const maxBytes = maxSizeMB * 1024 * 1024;

  if (!file) {
    return { valid: false, error: 'No file selected' };
  }

  if (!allowedMimeTypes.includes(file.type)) {
    return {
      valid: false,
      error: `Invalid audio format. Supported formats: MP3, WAV, WebM, OGG, FLAC, M4A, AAC, Opus, MP4`
    };
  }

  if (file.size > maxBytes) {
    return {
      valid: false,
      error: `File size exceeds ${maxSizeMB}MB limit. Your file: ${(file.size / 1024 / 1024).toFixed(2)}MB`
    };
  }

  return { valid: true };
};
